import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedGet } from '@/lib/api';
import {
  compatibleUnits,
  convertValue,
  type CompatibleUnit,
} from '@releve/shared/units';
import type { EmissionFactorDimensionRowsResponse } from '@releve/shared';

export interface CarbonConfig {
  id: string;
  table_id: string;
  multiplier_dimension_code: string | null;
  multiplier_dimension_label: string | null;
  multiplier_unit: string | null;
  /**
   * Dimension values pinned at account-link time. Merged with per-transaction
   * user dimensions via `mergeEmissionDimensions` (pin wins on key conflict).
   * Empty object = no pinning.
   */
  dimension_filter: Record<string, string>;
  /**
   * Canonical unit code the user is expected to enter per transaction (e.g.,
   * 'kg', 'night'). Compatible (same `MeasureKind` via the npm `convert`
   * library) with the linked factor's `input_unit_code`. Nullable during
   * rollout — when null the client falls back to the legacy freeform
   * `multiplier_unit` and skips conversion.
   */
  multiplier_unit_code: string | null;
  is_enabled: boolean;
}

interface TableColumn {
  id: string;
  column_code: string;
  display_label: string;
  column_type: string;
  is_required: boolean;
}

interface ColumnValue {
  id: string;
  column_id: string;
  value_code: string;
  display_label: string;
}

/**
 * Hook to fetch carbon configuration for an account
 */
export function useCarbonConfigForAccount(accountId?: string) {
  return useQuery({
    queryKey: ['account-carbon-config', accountId],
    queryFn: async () => {
      if (!accountId) return null;

      const { data, error } = await supabase
        .from('carbon_account_emission_tables')
        .select('*')
        .eq('account_id', accountId)
        .eq('is_enabled', true)
        .maybeSingle();

      if (error) throw error;
      return data as CarbonConfig | null;
    },
    enabled: !!accountId,
  });
}

/**
 * Hook to fetch table columns for a carbon emission table
 */
export function useCarbonTableColumns(tableId?: string) {
  return useQuery({
    queryKey: ['carbon-table-columns', tableId],
    queryFn: async () => {
      if (!tableId) return [];

      const { data, error } = await supabase
        .from('carbon_emission_factor_table_columns')
        .select('*')
        .eq('table_id', tableId)
        // Dimensions always render alphabetically (ascending) in selection UIs.
        .order('display_label');

      if (error) throw error;
      return data as TableColumn[];
    },
    enabled: !!tableId,
    // Reference data: factor-table columns and values change rarely.
    staleTime: 5 * 60_000,
  });
}

/**
 * Hook to fetch column values for select-type columns
 */
export function useCarbonColumnValues(columns?: TableColumn[]) {
  return useQuery({
    queryKey: ['carbon-column-values', columns?.map(c => c.id)],
    queryFn: async () => {
      if (!columns || columns.length === 0) return [];

      const columnIds = columns.filter(c => c.column_type === 'select').map(c => c.id);
      if (columnIds.length === 0) return [];

      const { data, error } = await supabase
        .from('carbon_emission_factor_column_values')
        .select('*')
        .in('column_id', columnIds)
        .eq('is_active', true)
        // Dimension values always render alphabetically (ascending) in dropdowns.
        .order('display_label');

      if (error) throw error;
      return data as ColumnValue[];
    },
    enabled: !!columns && columns.length > 0,
    // Reference data: factor-table columns and values change rarely.
    staleTime: 5 * 60_000,
  });
}

/**
 * Hook: fetch one representative factor's input/output unit codes for a table.
 * Used by AccountCarbonSettings to filter the multiplier dropdown and by the
 * transaction-side post-conversion path.
 *
 * Picks the first active factor with a non-null input_unit_code — almost all
 * system tables are homogeneous on this dimension (every NL waste row is
 * `/ton`, every hotel_stay row is `/night`, etc.). Mixed-unit tables exist:
 * `epa_mobile_co2` (gallon + scf) and `nl_co2emissiefactoren_energy`
 * (kWh + GJ + m3 since 20260812110000). When the operator picks a
 * mixed-unit table, they'll still see a dropdown filtered by the first
 * factor's unit — acceptable for v1 since per-transaction dim selection
 * narrows to a single factor anyway.
 */
export function useFactorTableInputUnit(tableId?: string) {
  return useQuery({
    queryKey: ['factor-table-input-unit', tableId],
    queryFn: async () => {
      if (!tableId) return null;
      const { data, error } = await supabase
        .from('carbon_emission_factors')
        .select('input_unit_code, output_unit_code')
        .eq('table_id', tableId)
        .eq('is_active', true)
        .not('input_unit_code', 'is', null)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as { input_unit_code: string | null; output_unit_code: string | null } | null;
    },
    enabled: !!tableId,
    // Factor metadata is reference data that rarely changes; skip refetch churn.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook: every active factor's `dimension_values` for a table. Drives the
 * activity form's left-to-right cascading dimension filters — each dropdown is
 * narrowed to values that actually occur in factors matching the selections to
 * its left. Reference data, so cached.
 *
 * Reads the dimension-rows ENDPOINT rather than Supabase directly. The old
 * direct read was a `select()` with NO limit, so a table past PostgREST's
 * 1,000-row cap silently lost dimension combinations — a dropdown value that
 * exists as a factor would simply not be offered. The endpoint reuses
 * `loadFactorIndex`, which applies the SAME two predicates the old query did
 * (`is_active` on the factor AND on its parent table, mirroring
 * `lookup_emission_factor` server-side: a soft-deleted table must not populate
 * dropdowns the resolver will then refuse), so the row set is unchanged apart
 * from no longer being truncated.
 *
 * Returns the BARE `dimension_values` objects — the array shape
 * `computeDimensionCascade` consumes — not the wire rows.
 *
 * The key keeps its `'factor-dimension-rows'` first slot (with the org id
 * inserted so the endpoint's org scope is reflected in the cache), which is
 * the prefix `invalidateEmissionFactorCaches` invalidates.
 */
export function useFactorDimensionRows(tableId?: string) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;
  return useQuery({
    queryKey: ['factor-dimension-rows', organizationId, tableId],
    queryFn: async (): Promise<Record<string, unknown>[]> => {
      if (!tableId || !organizationId) return [];
      const result = await authedGet<EmissionFactorDimensionRowsResponse>(
        `/api/v1/emissions/orgs/${organizationId}/factor-tables/${tableId}/dimension-rows`,
      );
      return result.rows.map((row) => row.dimension_values);
    },
    enabled: !!tableId && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook: dropdown options for `multiplier_unit_code` compatible with the
 * linked factor's input unit. Returns an empty array when the factor's
 * input_unit_code is NULL (admin hasn't configured it yet) — the UI falls
 * back to the legacy freeform field in that case.
 */
export function useCompatibleMultiplierUnits(tableId?: string): {
  units: CompatibleUnit[];
  factorInputUnitCode: string | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useFactorTableInputUnit(tableId);
  return useMemo(() => {
    const factorInput = data?.input_unit_code ?? null;
    return {
      units: factorInput ? compatibleUnits(factorInput) : [],
      factorInputUnitCode: factorInput,
      isLoading,
    };
  }, [data?.input_unit_code, isLoading]);
}

/**
 * Dimension-aware counterpart to useFactorTableInputUnit: resolves the SPECIFIC
 * factor for the given `dimensionValues` (via the same `lookup_emission_factor`
 * RPC `calculateCarbonEmission` peeks) and returns THAT factor's input/output
 * unit codes. This lets one table hold factors with different units — e.g.
 * Transport with passenger-mile vs vehicle-mile, or a material in kg — and have
 * the form's unit follow the chosen dimensions. Disabled (returns null) until at
 * least one dimension is chosen, so callers can fall back to the table-wide unit.
 */
export function useDimensionResolvedFactorUnit(
  tableId?: string,
  dimensionValues?: Record<string, string>,
) {
  const dimKey = JSON.stringify(dimensionValues ?? {});
  const enabled = !!tableId && Object.keys(dimensionValues ?? {}).length > 0;
  return useQuery({
    queryKey: ['dimension-resolved-factor-unit', tableId, dimKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('lookup_emission_factor', {
        p_table_id: tableId!,
        p_dimension_values: dimensionValues ?? {},
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return {
        input_unit_code: row.input_unit_code ?? null,
        output_unit_code: row.output_unit_code ?? null,
      } as { input_unit_code: string | null; output_unit_code: string | null };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // Retain the prior unit while the new selection resolves so the quantity
    // unit dropdown doesn't briefly empty (flicker) on each dimension change.
    placeholderData: keepPreviousData,
    // Tag failures so a broken unit-resolve (which silently empties the activity
    // form's unit dropdown) is filterable in Sentry rather than an anonymous RPC error.
    meta: { feature: 'impact-activity-unit-resolve' },
  });
}

/**
 * Resolve the SPECIFIC factor for the given `dimensionValues` and return its
 * displayable details (name + per-unit value + unit). Backs the activity form's
 * collapsible "factor details" panel. Independent of quantity — resolves as soon
 * as dimensions are chosen. Returns null until a row resolves.
 */
export function useResolvedFactor(
  tableId?: string,
  dimensionValues?: Record<string, string>,
) {
  const dimKey = JSON.stringify(dimensionValues ?? {});
  const enabled = !!tableId && Object.keys(dimensionValues ?? {}).length > 0;
  return useQuery({
    queryKey: ['resolved-factor', tableId, dimKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('lookup_emission_factor', {
        p_table_id: tableId!,
        p_dimension_values: dimensionValues ?? {},
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return {
        factor_id: row.factor_id as string,
        factor_name: (row.factor_name ?? '') as string,
        emission_value: Number(row.emission_value),
        unit_of_measure: (row.unit_of_measure ?? '') as string,
        input_unit_code: (row.input_unit_code ?? null) as string | null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // Keep the prior factor visible while the new dimension selection resolves,
    // so the details panel doesn't unmount/remount (flicker) on every change.
    placeholderData: keepPreviousData,
  });
}

/**
 * Dimension-aware variant of useCompatibleMultiplierUnits. Drives the quantity
 * unit dropdown from the factor resolved by `dimensionValues`; falls back to the
 * table-wide representative factor (useFactorTableInputUnit) before any dimension
 * is chosen. Use this where the caller has the live dimension selection (the
 * Impact Activities form). Leave useCompatibleMultiplierUnits for callers that
 * don't (e.g. AccountCarbonSettings).
 */
export function useCompatibleMultiplierUnitsForDimensions(
  tableId?: string,
  dimensionValues?: Record<string, string>,
): { units: CompatibleUnit[]; factorInputUnitCode: string | null; isLoading: boolean } {
  const hasDims = Object.keys(dimensionValues ?? {}).length > 0;
  const resolved = useDimensionResolvedFactorUnit(tableId, dimensionValues);
  const tableWide = useFactorTableInputUnit(tableId);

  return useMemo(() => {
    // Prefer the dimension-resolved factor's unit; fall back to the table-wide
    // representative factor until the dimensions resolve a row.
    const resolvedInput = hasDims ? resolved.data?.input_unit_code ?? null : null;
    const factorInput = resolvedInput ?? tableWide.data?.input_unit_code ?? null;
    return {
      units: factorInput ? compatibleUnits(factorInput) : [],
      factorInputUnitCode: factorInput,
      isLoading: resolved.isLoading || tableWide.isLoading,
    };
  }, [
    hasDims,
    resolved.data?.input_unit_code,
    resolved.isLoading,
    tableWide.data?.input_unit_code,
    tableWide.isLoading,
  ]);
}

/**
 * Calculate carbon emissions via the `calculate_carbon_emission` RPC, with
 * client-side unit reconciliation.
 *
 * - Pre-converts `multiplier` from `multiplierUnitCode` to the factor's
 *   native `input_unit_code` so the RPC math is correct.
 * - Post-converts the returned `calculated_emission` from the factor's
 *   `output_unit_code` to 'kg' so the rest of the app always displays
 *   kg CO2e.
 *
 * If either unit code is missing (custom factor not yet configured) the
 * respective conversion is a passthrough — behaviour identical to
 * pre-migration calls. Callers receive `IncompatibleUnitsError` on a
 * cross-category mismatch and must surface it (toast + Sentry).
 */
export async function calculateCarbonEmission(
  tableId: string,
  dimensionValues: Record<string, string>,
  multiplier: number,
  multiplierUnitCode: string | null = null,
) {
  // Step 1: peek at the factor's unit codes so we can pre-convert the
  // multiplier without a round trip to calculate_carbon_emission first.
  // `lookup_emission_factor` returns input_unit_code + output_unit_code.
  let factorInputUnit: string | null = null;
  let factorOutputUnit: string | null = null;
  if (multiplierUnitCode) {
    const { data: peek, error: peekError } = await supabase.rpc(
      'lookup_emission_factor',
      { p_table_id: tableId, p_dimension_values: dimensionValues },
    );
    if (peekError) throw peekError;
    const peekRow = Array.isArray(peek) ? peek[0] : peek;
    factorInputUnit = peekRow?.input_unit_code ?? null;
    factorOutputUnit = peekRow?.output_unit_code ?? null;
  }

  const normalizedMultiplier =
    multiplierUnitCode && factorInputUnit
      ? convertValue(multiplier, multiplierUnitCode, factorInputUnit)
      : multiplier;

  const { data, error } = await supabase.rpc('calculate_carbon_emission', {
    p_table_id: tableId,
    p_dimension_values: dimensionValues,
    p_multiplier: normalizedMultiplier,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return row;

  // Output normalization: the factor's value may be in metric tonnes of CO2e
  // (EPA `MT CO2e/short ton`). The rest of the app displays kg CO2e, so
  // normalize the emission total to kg. Use the output_unit_code the peek
  // returned if we have it; otherwise fall back to what the calc RPC
  // returned alongside the value (added by migration 20260417070745).
  //
  // `unit_of_measure` (the raw factor-value unit string, e.g. `kg CO2e/ton`)
  // is NOT overwritten — consumers display it next to the raw `emission_factor`
  // to describe the factor itself, not the calculated total.
  const outputUnit = factorOutputUnit ?? row.output_unit_code ?? null;
  const calculated =
    outputUnit && outputUnit !== 'kg'
      ? convertValue(row.calculated_emission, outputUnit, 'kg')
      : row.calculated_emission;

  return {
    ...row,
    // Overwrite the total with the kg-normalized value. The breakdown display
    // (emission_factor × multiplier) remains the raw RPC math — when a
    // conversion happened, that breakdown won't be arithmetically equal to
    // calculated_emission, but the total shown to the user is always in
    // kg CO2e. Accepted v1 trade-off.
    calculated_emission: calculated,
    // Expose the multiplier that actually went into the math so callers can
    // persist it (matches the activity-path invariant
    // `multiplier_value × emission_factor_value = calculated_emissions`).
    normalized_multiplier: normalizedMultiplier,
  };
}
